import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from 'src/components/ui/tooltip';
import { MapPinIcon } from '@phosphor-icons/react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import useCustomerLocations from 'src/api/accounts-receivable/customers/useCustomerLocations';
import { useMemo } from 'react';
import { useGT } from 'gt-react';

interface LocationPillProps {
  location?: {
    name?: string | null;
    address?: string | null;
  };
  locationId?: string;
  customerId?: string;
  onClick?: () => void;
}

const LocationPill: React.FC<LocationPillProps> = ({
  location: providedLocation,
  locationId,
  customerId,
  onClick,
}) => {
  const gt = useGT();
  const { data: locationsData, isLoading } = useCustomerLocations(
    customerId || '',
    !!locationId && !!customerId && !providedLocation,
  );

  const location = useMemo(() => {
    if (providedLocation) return providedLocation;
    if (!locationId || !locationsData?.locations) return null;
    return locationsData.locations.find((loc) => loc.id === locationId);
  }, [providedLocation, locationId, locationsData]);

  const tooltipContent = useMemo(() => {
    if (!location) return 'No address available';
    const parts = [];
    if (location.name) {
      parts.push(location.name);
    }
    if (location.address) {
      parts.push(location.address);
    }
    return parts.length > 0 ? parts.join('\n') : 'No address available';
  }, [location]);

  if (isLoading) {
    return (
      <MDBox
        sx={{
          fontSize: 12,
          backgroundColor: 'rgba(156, 39, 176, 0.08)',
          borderRadius: '16px',
          padding: '4px 8px',
          width: 'fit-content',
        }}
      >
        <MDTypography sx={{ fontSize: 12, color: 'rgba(156, 39, 176, 1)' }}>
          Loading...
        </MDTypography>
      </MDBox>
    );
  }

  if (!location) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <MDBox display="flex" alignItems="center" sx={{ cursor: 'default' }}>
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              fontSize: 12,
              backgroundColor: 'rgba(156, 39, 176, 0.08)',
              color: 'rgba(156, 39, 176, 1)',
              borderRadius: '16px',
              padding: '4px 8px',
              width: 'fit-content',
              cursor: onClick ? 'pointer' : undefined,
              transition: 'all 0.2s ease-in-out',
              ...(onClick && {
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.16)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: 'none',
                },
              }),
            }}
            onClick={onClick}
          >
            <MapPinIcon size={16} />
            <MDTypography sx={{ fontSize: 12, color: 'rgba(156, 39, 176, 1)' }}>
              {location.name ||
                location.address ||
                gt('Unnamed Location', {
                  context:
                    'fallback text when a location has no name or address',
                })}
            </MDTypography>
          </MDBox>
        </MDBox>
      </TooltipTrigger>
      <TooltipContent className="whitespace-pre-line max-w-[300px]">
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  );
};

export default LocationPill;
